import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, id, children }) => (
  <div>
    <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.2em] text-faint mb-2">
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full bg-white border border-line px-3.5 py-2.5 text-sm text-ink placeholder:text-faint/70 " +
  "focus:outline-none focus:border-pine transition-colors duration-200 rounded-none";

export const LeadForm = ({ variant }) => {
  const isPartnership = variant === "partnership";
  const tid = isPartnership ? "enquiry" : "contact";
  const [form, setForm] = useState({ name: "", company: "", email: "", country: "", territory: "", message: "" });
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const endpoint = isPartnership ? "enquiries" : "contact";
      const payload = isPartnership
        ? { name: form.name, company: form.company, email: form.email, country: form.country, territory: form.territory, message: form.message }
        : { name: form.name, email: form.email, company: form.company, message: form.message };
      const res = await axios.post(`${API}/${endpoint}`, payload);
      setReference(res.data.reference);
      toast.success(isPartnership ? "Enquiry received" : "Message sent", {
        description: `Reference ${res.data.reference}. We respond to qualified B2B enquiries directly.`,
      });
    } catch (err) {
      toast.error("Submission failed", { description: "Please try again, or email us directly." });
    } finally {
      setSending(false);
    }
  };

  if (reference) {
    return (
      <div data-testid={`${tid}-success`} className="border border-pine/30 bg-pinelight/60 p-8 text-center">
        <CheckCircle2 className="mx-auto text-pine" size={32} />
        <p className="mt-4 font-headline font-semibold text-lg text-ink">
          {isPartnership ? "Enquiry received" : "Message sent"}
        </p>
        <p className="mt-2 text-sm text-smoke">
          Reference <span className="font-mono text-pine font-medium">{reference}</span>. Your message has been
          routed to our Madison office; we reply to qualified business enquiries directly.
        </p>
        <button
          data-testid={`${tid}-send-another-button`}
          onClick={() => { setReference(null); setForm({ name: "", company: "", email: "", country: "", territory: "", message: "" }); }}
          className="mt-6 text-[13px] font-medium text-pine hover:text-pinehover underline underline-offset-4"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form data-testid={`${tid}-form`} onSubmit={submit} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full name *" id={`${tid}-name`}>
          <input data-testid={`${tid}-name-input`} id={`${tid}-name`} required maxLength={120} className={inputCls} value={form.name} onChange={set("name")} placeholder="Dr. Jane Doe" />
        </Field>
        <Field label="Company *" id={`${tid}-company`}>
          <input data-testid={`${tid}-company-input`} id={`${tid}-company`} required={isPartnership} maxLength={160} className={inputCls} value={form.company} onChange={set("company")} placeholder="Company Ltd." />
        </Field>
      </div>
      <Field label="Work email *" id={`${tid}-email`}>
        <input data-testid={`${tid}-email-input`} id={`${tid}-email`} type="email" required className={inputCls} value={form.email} onChange={set("email")} placeholder="j.doe@company.com" />
      </Field>
      {isPartnership && (
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Country (headquarters) *" id={`${tid}-country`}>
            <input data-testid={`${tid}-country-input`} id={`${tid}-country`} required maxLength={120} className={inputCls} value={form.country} onChange={set("country")} placeholder="Germany" />
          </Field>
          <Field label="Territory of interest *" id={`${tid}-territory`}>
            <input data-testid={`${tid}-territory-input`} id={`${tid}-territory`} required maxLength={200} className={inputCls} value={form.territory} onChange={set("territory")} placeholder="EU, UK, APAC…" />
          </Field>
        </div>
      )}
      <Field label="Message *" id={`${tid}-message`}>
        <textarea
          data-testid={`${tid}-message-input`}
          id={`${tid}-message`}
          required
          maxLength={4000}
          rows={5}
          className={`${inputCls} resize-y`}
          value={form.message}
          onChange={set("message")}
          placeholder={isPartnership ? "Tell us about your portfolio, intended product format and timeline." : "How can we help?"}
        />
      </Field>
      <button
        data-testid={`${tid}-submit-button`}
        type="submit"
        disabled={sending}
        className="inline-flex items-center gap-2 bg-pine hover:bg-pinehover disabled:opacity-60 text-white text-sm font-medium px-6 py-3 transition-colors duration-200"
      >
        {sending && <Loader2 size={15} className="animate-spin" />}
        {isPartnership ? "Submit partnership enquiry" : "Send message"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-faint">
        B2B evaluation only · No consumer enquiries
      </p>
    </form>
  );
};
